import { Check } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';

import { Combobox } from '@apps/web/components/ui/combobox';
import { CommandGroup, CommandItem } from '@apps/web/components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@apps/web/components/ui/popover';
import { cn } from '@apps/web/lib/utils';

export type StatusOption<T extends string> = {
    readonly value: T;
    readonly label: string;
    readonly dotColor: string;
};

type BaseStatusPickerProps<T extends string> = {
    readonly options: ReadonlyArray<StatusOption<T>>;
    readonly value: T | null;
    readonly onSelect: (value: T) => void;
    readonly disabled?: boolean;
    readonly contentClassName?: string;
    readonly contentSide?: 'top' | 'right' | 'bottom' | 'left';
    readonly contentAlign?: 'start' | 'center' | 'end';
};

type ChildrenTriggerProps<T extends string> = BaseStatusPickerProps<T> & {
    readonly children: ReactNode;
    readonly displayValue?: never;
    readonly placeholder?: never;
    readonly triggerClassName?: never;
    readonly searchable?: never;
    readonly searchPlaceholder?: never;
    readonly ariaLabel?: never;
};

type BuiltInTriggerProps<T extends string> = BaseStatusPickerProps<T> & {
    readonly children?: never;
    readonly displayValue?: ReactNode;
    readonly placeholder?: string;
    readonly triggerClassName?: string;
    readonly searchable?: boolean;
    readonly searchPlaceholder?: string;
    readonly ariaLabel?: string;
};

type StatusPickerProps<T extends string> =
    | ChildrenTriggerProps<T>
    | BuiltInTriggerProps<T>;

function StatusPickerOptions<T extends string>({
    options,
    value,
    onSelect,
    onClose,
}: {
    options: ReadonlyArray<StatusOption<T>>;
    value: T | null;
    onSelect: (value: T) => void;
    onClose: () => void;
}) {
    return (
        <div className="flex flex-col gap-0.5">
            {options.map((option) => {
                const isSelected = value === option.value;

                return (
                    <button
                        key={option.value}
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onSelect(option.value);
                            onClose();
                        }}
                        className={cn(
                            'hover:bg-accent flex items-center gap-3 rounded-sm px-2 py-2 text-sm',
                            isSelected && 'bg-accent',
                        )}
                    >
                        <span
                            className={cn(
                                'inline-block h-2 w-2 rounded-full',
                                option.dotColor,
                            )}
                        />
                        <span className="flex-1 text-left">{option.label}</span>
                        {isSelected && (
                            <Check className="text-primary h-4 w-4" />
                        )}
                    </button>
                );
            })}
        </div>
    );
}

function ChildrenTriggerStatusPicker<T extends string>({
    options,
    value,
    onSelect,
    disabled = false,
    contentClassName,
    contentSide = 'left',
    contentAlign = 'start',
    children,
}: ChildrenTriggerProps<T>) {
    const [open, setOpen] = useState(false);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger
                asChild={true}
                disabled={disabled}
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                data-row-click-ignore="true"
            >
                {children}
            </PopoverTrigger>
            <PopoverContent
                className={cn('w-[160px] p-1.5', contentClassName)}
                side={contentSide}
                align={contentAlign}
                onClick={(e) => e.stopPropagation()}
            >
                <StatusPickerOptions
                    options={options}
                    value={value}
                    onSelect={onSelect}
                    onClose={() => setOpen(false)}
                />
            </PopoverContent>
        </Popover>
    );
}

function BuiltInTriggerStatusPicker<T extends string>({
    options,
    value,
    onSelect,
    disabled = false,
    contentClassName,
    contentSide = 'left',
    contentAlign = 'start',
    displayValue,
    placeholder = '—',
    triggerClassName,
    searchable = false,
    searchPlaceholder = 'Search...',
    ariaLabel,
}: BuiltInTriggerProps<T>) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const optionMap = useMemo(
        () => new Map(options.map((opt) => [opt.value, opt] as const)),
        [options],
    );

    const selectedOption = value ? (optionMap.get(value) ?? null) : null;

    const filteredOptions = useMemo(() => {
        if (!searchable || !search.trim()) {
            return options;
        }
        const query = search.trim().toLowerCase();
        return options.filter((option) =>
            option.label.toLowerCase().includes(query),
        );
    }, [options, search, searchable]);

    const triggerContent = displayValue ?? (
        <span className="inline-flex items-center gap-1.5">
            {selectedOption ? (
                <span
                    className={cn(
                        'h-2 w-2 rounded-full',
                        selectedOption.dotColor,
                    )}
                />
            ) : null}
            <span>{selectedOption?.label ?? placeholder}</span>
        </span>
    );

    return (
        <Combobox
            open={open}
            onOpenChange={(next) => {
                setOpen(next);
                if (!next) {
                    setSearch('');
                }
            }}
            displayValue={triggerContent}
            placeholder={placeholder}
            disabled={disabled}
            hideSearch={!searchable}
            searchValue={search}
            onSearchValueChange={setSearch}
            searchPlaceholder={searchPlaceholder}
            triggerClassName={cn(
                'h-auto border-0 shadow-none px-2.5 py-1 text-xs font-medium gap-1.5 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0',
                triggerClassName,
            )}
            triggerProps={{
                'aria-label': ariaLabel,
                'data-row-click-ignore': 'true',
            }}
            contentClassName={cn('w-[160px] p-1.5', contentClassName)}
            contentSide={contentSide}
            contentAlign={contentAlign}
            matchTriggerWidth={false}
            fullWidth={false}
            showCaret={false}
            commandProps={{ shouldFilter: false }}
        >
            <CommandGroup>
                {filteredOptions.map((option) => {
                    const isSelected = value === option.value;

                    return (
                        <CommandItem
                            key={option.value}
                            value={option.value}
                            onPointerDown={(event) => {
                                event.stopPropagation();
                            }}
                            onClick={(event) => {
                                event.stopPropagation();
                            }}
                            data-row-click-ignore="true"
                            onSelect={() => {
                                onSelect(option.value);
                                setOpen(false);
                                setSearch('');
                            }}
                            className="flex items-center gap-3 rounded-sm px-2 py-2 text-sm"
                        >
                            <span
                                className={cn(
                                    'inline-block h-2 w-2 rounded-full',
                                    option.dotColor,
                                )}
                            />
                            <span className="flex-1">{option.label}</span>
                            {isSelected && (
                                <Check className="text-primary h-4 w-4" />
                            )}
                        </CommandItem>
                    );
                })}
            </CommandGroup>
        </Combobox>
    );
}

export function StatusPicker<T extends string>(props: StatusPickerProps<T>) {
    if ('children' in props && props.children) {
        return <ChildrenTriggerStatusPicker {...props} />;
    }

    return (
        <BuiltInTriggerStatusPicker {...(props as BuiltInTriggerProps<T>)} />
    );
}
